import { useMutation, useApolloClient } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { Button, Modal } from "../../ui";
import { FETCH_LISTS as fetchLists } from "../../../graphql/query/List";
import { COPY_LIST } from "../../../graphql/mutation/List";
import {
  LIST_TYPES,
  isImageListType,
  isWorkingListType,
  getImageListType,
  getEntityListType,
  canCopyListType,
} from "../../../constants/listTypes";
import CopyListModalBody from "./CopyListModalBody";

const CopyList = props => {
  const { listId, workingListId, onSuccess, typeId, sourceTypeId } = props || {};
  const client = useApolloClient();
  const navigate = useNavigate();
  const finalListId = listId || workingListId;
  const finalSourceTypeId = sourceTypeId || typeId;
  
  // Determine if we can copy this list type
  const canCopy = finalSourceTypeId ? canCopyListType(finalSourceTypeId) : false;
  const isSourceImageList = finalSourceTypeId ? isImageListType(finalSourceTypeId) : false;
  const targetTypeId = finalSourceTypeId
    ? (isSourceImageList ? getEntityListType(finalSourceTypeId) : getImageListType(finalSourceTypeId))
    : null;

  // Build refetch queries based on source and target types
  const refetchQueriesList = [];
  if (finalSourceTypeId) {
    refetchQueriesList.push({
      query: fetchLists(),
      variables: { input: { typeId: finalSourceTypeId } },
      fetchPolicy: "network-only",
    });
  }
  if (targetTypeId) {
    refetchQueriesList.push({
      query: fetchLists(),
      variables: { input: { typeId: targetTypeId } },
      fetchPolicy: "network-only",
    });
  }
  
  const [copyList, { loading }] = useMutation(COPY_LIST({}), {
    refetchQueries: refetchQueriesList,
    awaitRefetchQueries: true,
    onCompleted: async () => {
      // Refetch all FetchLists queries regardless of variables to ensure cache is cleared
      await client.refetchQueries({
        include: ["FetchLists"],
      });
    },
  });

  const handleCopy = closeModal => async (e) => {
    e?.preventDefault?.();
    
    if (!finalListId) {
      console.error("Cannot copy list: listId is missing");
      return;
    }

    if (!canCopy) {
      console.error("This list type does not support copying");
      return;
    }

    // Determine if we're copying from a saved list (listId present) or working list (only workingListId)
    const isCopyingFromSavedList = !!listId;

    const res = await copyList({
      variables: {
        input: {
          listId: finalListId,
          ...(targetTypeId ? { targetTypeId } : {}),
        },
      },
    });
    
    if (res?.data?.copyList?.success) {
      const newList = res?.data?.copyList?.list;
      const newListId = newList?.id;
      const newListTypeId = newList?.typeId;
      closeModal();
      
      // Navigate to the newly created list
      if (newListId && newListTypeId) {
        const isNewListImageList = isImageListType(newListTypeId);
        const isNewListSaved = !isWorkingListType(newListTypeId);
        
        // Determine navigation URL based on list type and module
        let baseUrl = "/instance"; // Default to instance
        let statusKey = "selectListWorking";
        let listIdParam = "listId";
        
        if (newListTypeId === LIST_TYPES.ARTWORK || newListTypeId === LIST_TYPES.ARTWORK_WORKING) {
          baseUrl = "/artworks";
        } else if (newListTypeId === LIST_TYPES.ARTWORK_IMAGE || newListTypeId === LIST_TYPES.ARTWORK_IMAGE_WORKING) {
          baseUrl = "/artworks";
          statusKey = "imageSelectListWorking";
          listIdParam = "imageListId";
        } else if (isNewListImageList) {
          statusKey = "imageSelectListWorking";
          listIdParam = "imageListId";
        }
        
        if (isNewListSaved && isCopyingFromSavedList) {
          // Navigate to the saved list
          navigate(`${baseUrl}?status=${statusKey}&${listIdParam}=${newListId}`);
        } else {
          // Use the onSuccess callback for working lists or fallback
          onSuccess?.();
        }
      } else {
        // Fallback: if we can't get the new list ID, use onSuccess
        onSuccess?.();
      }
    } else if (res?.data?.copyList?.error) {
      console.error("Error copying list:", res.data.copyList.error);
    }
  };

  const modalProps = {
    title: "",
    scale: "md",
    hideCloseButton: true,
    closeOnBackdrop: false,
    renderBody: ({ closeModal }) => {
      return (
        <CopyListModalBody
          loading={loading}
          listName={null}
          onCopy={handleCopy(closeModal)}
          onCancel={closeModal}
          isImageToEntity={isSourceImageList}
        />
      );
    },
  };

  if (!finalListId || !canCopy) {
    return null;
  }

  const buttonLabel = isSourceImageList ? "Copy to Select List" : "Copy to Image Select List";

  return (
    <Modal {...modalProps}>
      <Button
        label={buttonLabel}
        type="button"
        className="flex items-center"
      />
    </Modal>
  );
};

export default CopyList;

